import type { FunctionComponent } from 'react'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { ButtonLink, Link } from '@which/seatbelt'

import { ToolWrapper } from '../../../../shared'
import { ImageWithQuestion } from '../../../../shared/components/ImageWithQuestion'
import { Loader } from '../../../../shared/components/Loader'
import { PageWithText } from '../../../../shared/components/PageWithText'
import { formatToolName } from '../../../../shared/utils/formatToolName'
import { initaliseDataLayer, pageViewDataLayer } from '../../../../shared/utils/tracking'
import { FollowUpAppointmentFlow } from '../../pageFlowData'
import { followUpAppointmentData } from './data'
import styles from './FollowUpAppointment.module.scss'
import type { FollowUpAppointmentResponse } from './getIFrameData'
import { getIFrameData } from './getIFrameData'

type FollowUpAppointmentType = {
  toolName: string
}

const mapAppointmentCategoryToLabel: Record<'legal' | 'tech' | 'money', string> = {
  legal: 'Legal',
  tech: 'Tech Support',
  money: 'Money',
}

const mapAppointmentCategoryToHref: Record<'legal' | 'tech' | 'money', string> = {
  legal:
    'https://legalservice.which.co.uk/?utm_source=Which&utm_medium=Referral&utm_campaign=main_nav&utm_content=Legal',
  tech: 'https://signup.which.co.uk/wlp-tech-support',
  money: 'https://signup.which.co.uk/wlp-money',
}

export const FollowUpAppointment: FunctionComponent<FollowUpAppointmentType> = ({ toolName }) => {
  useEffect(() => {
    pageViewDataLayer(FollowUpAppointmentFlow.VIEW_NAME)
  }, [])

  const [iFrameData, setIFrameData] = useState<
    ({ state: 'loaded' } & FollowUpAppointmentResponse) | { state: 'error' } | { state: 'loading' }
  >({ state: 'loading' })

  const location = useLocation()
  const useQuery = () => {
    return new URLSearchParams(location?.search)
  }

  const query = useQuery()
  const appointmentId = query?.get('appointmentId')

  useEffect(() => {
    const createIFrame = async () => {
      if (appointmentId) {
        setIFrameData({ state: 'loading' })
        try {
          const iFrameResponse = await getIFrameData(appointmentId)
          setIFrameData({ state: 'loaded', ...iFrameResponse })
        } catch (error) {
          setIFrameData({
            state: 'error',
          })
        }
      } else {
        setIFrameData({ state: 'error' })
      }
    }
    createIFrame()
  }, [appointmentId])

  const { imgObj, title, standFirst } = followUpAppointmentData

  return (
    <ToolWrapper>
      <Helmet>
        <meta content="noindex" name="robots" />
        <script>{initaliseDataLayer(formatToolName(toolName))}</script>
        <title>Follow up Appointment</title>
        <meta content={'Online appointment bookingOnline appointment booking'} name="title" />
        <meta content="summary" name="twitter:card" />
        <meta content={'Book your follow up online appointment booking'} name="description" />
        <meta content="website" property="og:type" />
        <meta content={'https://www.which.co.uk/tool/book-follow-up'} property="og:url" />
        <meta content={'Online appointment booking'} property="og:title" />
        <meta
          content={'Book your follow up online appointment booking'}
          property="og:description"
        />
        <meta content={'https://www.which.co.uk/tool/book-follow-up'} property="twitter:url" />
        <meta content={'Online appointment booking'} property="twitter:title" />
        <meta
          content={'Book your follow up online appointment booking'}
          property="twitter:description"
        />
      </Helmet>
      {iFrameData.state === 'loading' && <Loader />}
      {iFrameData.state === 'error' && (
        <p className={styles.followUpAppointmentStandFirst} data-testid="typography-body">
          Sorry, something went wrong
        </p>
      )}
      {iFrameData.state === 'loaded' && !iFrameData.isSupported && (
        <>
          <PageWithText title={'A follow-up appointment cannot be booked'} />
          <p className={styles.followUpAppointmentStandFirst} data-testid="typography-body">
            <Link href={'/tool/book-appointment'}>Click here to book a new appointment</Link>
          </p>
        </>
      )}
      {iFrameData.state === 'loaded' && iFrameData.isSupported && !iFrameData.isEntitled && (
        <div className={styles.followUpAppointment}>
          <PageWithText
            title={`You’ll need to renew your ${mapAppointmentCategoryToLabel[iFrameData.category]} membership to book a follow up appointment`}
          />
          <p className={styles.followUpAppointmentStandFirst} data-testid="typography-body">
            {`It looks like your subscription to this service has expired. Our expert advice is part of our ${mapAppointmentCategoryToLabel[iFrameData.category]} membership, so you’ll need to `}
            <Link href={mapAppointmentCategoryToHref[iFrameData.category]}>
              renew your subscription
            </Link>
            {' in order to make a follow up appointment.'}
          </p>
          <ButtonLink
            className={styles.followUpAppointmentRenew}
            href={mapAppointmentCategoryToHref[iFrameData.category]}
          >
            Renew Subscription
          </ButtonLink>
        </div>
      )}
      {iFrameData.state === 'loaded' && iFrameData.isSupported && iFrameData.isEntitled && (
        <>
          <ImageWithQuestion image={imgObj} title={title} />
          <p className={styles.followUpAppointmentStandFirst} data-testid="typography-body">
            {standFirst}
          </p>
          <iframe
            allow="geolocation"
            className={styles.followUpAppointmentFrame}
            referrerPolicy="no-referrer"
            src={iFrameData.sameExpertAppointmentUrl}
            title="Appointment Choice"
          />
        </>
      )}
    </ToolWrapper>
  )
}
